import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { type CallState } from '../lib/callState';

const LS_LEAD_SURVEY_FLOW = 'leadSurvey.flow';

type LeadSurveyFlowStore = {
  bannerCtaIdx: number;
  callState: CallState;
  surveyBypassedAt: null | number;
  setBannerCtaIdx: (index: number) => void;
  setCallState: (state: CallState) => void;
  setSurveyBypassedAt: (at: null | number) => void;
};

/**
 * Per-device persistence for the Trial Lead Survey flow state (callState + banner CTA rotation index).
 *
 * Survey-completion + qualification are NOT stored here — they derive from the backend-authoritative
 * `user.lead_survey_completed` / `user.lead_survey_qualified`. Only the purely client-side bits live in
 * localStorage: which call action the user took, which CTA to show next, and `surveyBypassedAt` — the
 * timestamp of the escape hatch a user takes after repeated POST failures, persisted so the forced
 * survey can't re-trap them on reload. It is stored as a timestamp (not a boolean) so `useLeadSurvey`
 * can expire it after a TTL: the bypass is a temporary reprieve, not a permanent exemption — once the
 * window elapses the survey is shown again, so a lead whose backend has since recovered is re-prompted
 * instead of being suppressed forever. No clientStorage / no network: no CORS, no 500s, no rollback.
 */
export const useLeadSurveyFlowStore = create<LeadSurveyFlowStore>()(
  persist(
    (set) => ({
      bannerCtaIdx: 0,
      callState: 'idle',
      surveyBypassedAt: null,
      setBannerCtaIdx: (bannerCtaIdx) => set({ bannerCtaIdx }),
      setCallState: (callState) => set({ callState }),
      setSurveyBypassedAt: (surveyBypassedAt) => set({ surveyBypassedAt }),
    }),
    {
      name: LS_LEAD_SURVEY_FLOW,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
